/**
 * EventManager - Centralized event listener management
 *
 * Responsibilities:
 * - Keyboard events (fullscreen toggle, ESC for simulation mode)
 * - Window resize handling
 * - Visibility/focus management
 * - Fallback timer for backgrounded tabs
 */

import { RENDERING } from '../config/constants.js';
import { OfflineSimulator } from '../game/OfflineSimulator.js';

export class EventManager {
    constructor(app) {
        this.app = app;

        // Track when app was backgrounded
        this.backgroundedAt = null;

        // Fallback timer for backgrounded tabs
        this.fallbackTimerInterval = null;
        this.isUsingFallbackTimer = false;
    }

    /**
     * Set up all event listeners
     */
    setup() {
        this._setupKeyboardEvents();
        this._setupResizeHandler();
        this._setupVisibilityHandler();
        this._setupFocusHandler();
    }

    /**
     * Check if keyboard capture should be blocked
     * (when typing in inputs, modals are open, or game hasn't started)
     */
    _shouldBlockKeyboardCapture(e) {
        // Don't capture if typing in an input or textarea
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return true;

        // Don't capture if a modal overlay is present
        if (document.querySelector('.cf-modal-overlay')) return true;

        return false;
    }

    /**
     * Set up keyboard event handlers
     */
    _setupKeyboardEvents() {
        window.addEventListener('keydown', (e) => {
            // Block keyboard capture when typing or modal is open
            if (this._shouldBlockKeyboardCapture(e)) return;

            // Only capture game-related keys when game is running
            if (!this.app.gameStarted) return;

            // Prevent space bar scrolling during gameplay
            if (e.key === ' ') e.preventDefault();

            // Toggle fullscreen with F key
            if (e.key === 'f' || e.key === 'F') {
                this.app.toggleFullscreen();
                e.preventDefault();
            }

            // ESC key to enter offline simulation mode (desktop only)
            if (e.key === 'Escape' && !this.app.isMobile) {
                e.preventDefault();
                if (this.app.offlineDisplayManager) {
                    this.app.offlineDisplayManager.showStartSimulatingModal();
                }
            }
        });
    }

    /**
     * Set up window resize handler
     */
    _setupResizeHandler() {
        window.addEventListener('resize', () => {
            // Guard: camera may not be initialized yet during startup
            if (!this.app.camera) return;

            // Update camera aspect ratio
            this.app.camera.aspect = window.innerWidth / window.innerHeight;
            this.app.camera.updateProjectionMatrix();

            // Recalculate dimensions using resolution scale
            const renderWidth = Math.round(window.innerWidth * RENDERING.RESOLUTION_SCALE);
            const renderHeight = Math.round(window.innerHeight * RENDERING.RESOLUTION_SCALE);

            this.app.renderer.setSize(renderWidth, renderHeight, false);
        });
    }

    /**
     * Set up visibility change handler (tab backgrounding)
     */
    _setupVisibilityHandler() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this._handleTabHidden();
            } else {
                this._handleTabVisible();
            }
        });

        // Save simulator state on tab close for accurate resume calculation
        window.addEventListener('pagehide', () => {
            this._saveSimulatorStateForResume();
        });

        window.addEventListener('beforeunload', () => {
            this._saveSimulatorStateForResume();
        });
    }

    /**
     * Save simulator state to localStorage for tab close recovery
     * This allows resume() to batch-simulate elapsed time accurately
     */
    _saveSimulatorStateForResume() {
        if (this.app.offlineSimulator?.isRunning || this.app.offlineSimulator?.isSuspended) {
            this.app.offlineSimulator.saveStateForUnload();
        }
    }

    /**
     * Handle tab becoming hidden
     */
    _handleTabHidden() {
        this.backgroundedAt = Date.now();

        if (this.app.gameStarted) {
            // BOTH MOBILE AND DESKTOP: Enter offline simulation mode when backgrounded
            // This ensures clean state (modals closed) and consistent behavior
            console.log('[EventManager] Tab backgrounded - entering offline mode');
            this.app.unloadGame();
        } else if (this.app.offlineSimulator?.isRunning) {
            // On main menu with simulator running - suspend it
            console.log('[EventManager] Tab hidden while simulating - suspending');
            this.app.offlineSimulator.suspend();
        }
    }

    /**
     * Handle tab becoming visible
     */
    _handleTabVisible() {
        const backgroundDuration = this.backgroundedAt
            ? ((Date.now() - this.backgroundedAt) / 1000).toFixed(1) + 's'
            : 'unknown';
        console.log('[EventManager] TAB VISIBLE - was backgrounded for:', backgroundDuration);

        // Use requestAnimationFrame to ensure browser is fully responsive
        // This helps Firefox recover properly from backgrounding
        requestAnimationFrame(() => {
            if (this.app.gameStarted) {
                // Game is still running - this shouldn't normally happen since both
                // mobile and desktop now unload when backgrounded, but handle as fallback
                console.log('[EventManager] Game still running (unexpected), stopping fallback timer');
                this.stopFallbackTimer();
                this.backgroundedAt = null;
            } else if (this.app.offlineSimulator?.isSuspended) {
                // Resume suspended simulator (seamless batch calculation)
                console.log('[EventManager] Tab visible - resuming suspended simulator');
                this.app.offlineSimulator.resume();
            } else if (!this.app.gameStarted) {
                // Fallback: Check if localStorage has state for current slot but object isn't suspended
                // This can happen when mobile browser freezes/unfreezes tab in unexpected ways
                const slotKey = this.app.gameState?.saveManager?.getActiveSaveKey() || 'roadTripSave_anonymous';
                const hasSimulatorState = localStorage.getItem(`offlineSimulatorState_${slotKey}`) !== null;
                if (!hasSimulatorState) return;
                console.log('[EventManager] Tab visible - recovering simulator from localStorage');
                if (!this.app.offlineSimulator) {
                    this.app.offlineSimulator = new OfflineSimulator(this.app.gameState);
                }
                if (this.app.offlineSimulator.resume()) {
                    // Set up callbacks if not already set
                    if (!this.app.offlineSimulator.onUpdate && this.app.offlineDisplayManager) {
                        this.app.offlineSimulator.onUpdate = (progress) => {
                            this.app.offlineDisplayManager.updateDisplay(progress);
                        };
                        this.app.offlineSimulator.onFuelDepleted = () => {
                            this.app.offlineDisplayManager.handleFuelDepleted();
                        };
                        this.app.offlineDisplayManager.setupSpeedTooltip();
                        this.app.offlineDisplayManager.setupPrestigeBadge();
                    }
                }
            }
        });
    }

    /**
     * Set up focus handler (Firefox-specific recovery)
     */
    _setupFocusHandler() {
        // Firefox-specific: Ensure JavaScript is responsive after focus
        // This helps recover from states where event handlers seem frozen
        window.addEventListener('focus', () => {
            // Force a reflow to wake up the browser's rendering/event system
            document.body.offsetHeight;
        });
    }

    /**
     * Start fallback timer for backgrounded desktop tabs.
     *
     * Architecture Note (Phase 6 Timer Consolidation):
     * This fallback timer drives the main animate() loop when tabs are backgrounded,
     * NOT GameLoop subscribers. GameLoop.js handles its own fallback for subscribed
     * tick callbacks. The separation is intentional:
     * - EventManager fallback: main.js animate() loop continuation
     * - GameLoop fallback: subscriber callbacks (physics, systems, etc.)
     *
     * We keep this separate because:
     * 1. EventManager is specifically for the app-level backgrounded tab behavior
     * 2. GameLoop fallback handles its own tick propagation
     * 3. Merging would create tight coupling between app lifecycle and system ticks
     */
    startFallbackTimer() {
        if (this.isUsingFallbackTimer) return;

        this.isUsingFallbackTimer = true;
        console.log('[EventManager] Starting fallback timer for backgrounded tab');

        // Use setInterval as fallback since requestAnimationFrame pauses
        this.fallbackTimerInterval = setInterval(() => {
            if (!this.app.gameStarted || !this.app.driveStateMachine) return;

            // Simulate a frame with estimated delta
            const deltaSeconds = 0.1; // 100ms interval
            this.app.driveStateMachine.update(deltaSeconds);

            // Save periodically while backgrounded
            this.app.gameState.save();
        }, 100);
    }

    /**
     * Stop fallback timer
     */
    stopFallbackTimer() {
        if (!this.isUsingFallbackTimer) return;

        this.isUsingFallbackTimer = false;
        if (this.fallbackTimerInterval) {
            clearInterval(this.fallbackTimerInterval);
            this.fallbackTimerInterval = null;
        }
        console.log('[EventManager] Stopped fallback timer');
    }

    /**
     * Cleanup
     */
    dispose() {
        this.stopFallbackTimer();
    }
}
